import { bundlePageFile, isRoutedOwnerPage, NOT_FOUND_PREVIEW_PATH } from "@stackje/site-builder/bundle-pages";
import { createHttpShopApi } from "./api/httpBuilderApi";
import { ShopView } from "./components/shop/ShopView";
import { ShopSettingsView } from "./components/shop/SettingsView";
import { ProductListSkeleton, ShopFieldsSkeleton } from "./components/shop/ShopLoading";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Plus, X } from "lucide-react";
import { usePostSelectionRequest } from "./hooks/usePostSelectionRequest";
import { useBuilderNavigation } from "./hooks/useBuilderNavigation";
import type { WorkspaceNavigationController } from "./lib/workspaceNavigation";
import type { BuilderApi, NewComment, OpenPanel, OpenPost, OwnerCommand } from "./api/contract";
import { useResource } from "./hooks/useResource";
import { AreaError, AreaHead, AreaPage, FieldsSkeleton, ListSkeleton, LoadingActivity } from "./components/area";
import { useConversation } from "./hooks/useConversation";
import { useReferences } from "./hooks/useReferences";
import { usePublication, type PublicationState } from "./hooks/usePublication";
import { useServingChange } from "./hooks/useServingChange";
import { useActionFollower } from "./hooks/useActionFollower";
import { useWorkspace } from "./hooks/useWorkspace";
import { useUndo } from "./hooks/useUndo";
import { useWorkspaceMode } from "./hooks/useWorkspaceMode";
import { usePeek } from "./hooks/usePeek";
import { useWorkingLabel } from "./hooks/useWorkingLabel";
import { useKeyboardLayout } from "./hooks/useKeyboardLayout";
import { siteHasFeature } from "./lib/features";
import { areaForShortcut, shortcutHint, switcherLabel, switcherRows, type Area } from "./lib/areas";
import { workspaceAccess } from "./lib/access";
import { outlinePageTitles } from "./lib/outlinePages";
import { submitConfirmed } from "./lib/serving";
import { bubbleLabel, describeLiveStatus, staxMood, type Tone } from "./lib/actionCopy";
import { displayOrigin, draftPreviewRoot, hasHomePage, liveStatus, releasePreviewRoot, startPath, waitingReview } from "./lib/workspaceState";
import { historyEntries, type HistoryEntry, type RestorePart } from "./components/HistoryView";
import { SiteDrawer } from "./components/SiteDrawer";
import { PhotoAdd } from "./components/PhotoAdd";
import { ChangesView } from "./components/ChangesView";
import { useLibrary } from "./hooks/useLibrary";
import { usePublishing } from "./hooks/usePublishing";
import { AdminPanel } from "./components/AdminPanel";
import { BlankCanvas } from "./components/BlankCanvas";
import { DismissButton, Peek, ToastIcon, ToastTimer, UndoToast } from "./components/Notices";
import { PageOutlineSkeleton, Skeleton } from "./components/Skeleton";
import { Spinner } from "./components/Spinner";
import { Composer, ConversationBody } from "./components/Conversation";
import { OutcomeCard } from "./components/OutcomeCard";
import { resultComments } from "./lib/simpleChat";
import { PreviewFrame, type CommentDrop, type FrameAnchor, type PinView, type TextPick, type SectionSelection, type SectionSummary, type PhotoPick, type PointPath } from "./components/PreviewFrame";
import { CommentCard, CommentTray, pinLabel } from "./components/Comments";
import { PreviewView } from "./components/PreviewView";
import { startingPreviewSize } from "./lib/previewSize";
import { newCommentId, useComments } from "./hooks/useComments";
import { TextEditor } from "./components/TextEditor";
import { FIELDS } from "./components/ContentView";
import { bridgeMode, dropAt, stepName } from "./lib/pointing";
import { itemRecord } from "./lib/itemFields";
import { addSectionNote, insertCollections, sectionGaps } from "./lib/insert";
import { SectionToolbar } from "./components/SectionToolbar";
import { AddSectionPicker } from "./components/AddSectionPicker";
import { PhotoActions } from "./components/PhotoActions";
import { PointTrail } from "./components/PointTrail";
import { ItemFields } from "./components/ItemFields";
import { PhotoCrop } from "./components/PhotoCrop";
import { cropOutputSize, cropToPixels, type CropBox } from "./lib/crop";
import { changeMarkers, unpublishedChanges, unpublishedLabel } from "./lib/changes";
import { SectionInspector } from "./components/SectionInspector";
import { livePanel, publishPanel, PublishMark } from "./components/PublishFlow";
import { MobileTools, ModeSwitch, WorkspaceBar } from "./components/WorkspaceBar";
import { publishControl, publishStatusLine } from "./lib/publishControl";
import { WorkspaceProblem } from "./components/WorkspaceProblem";
import { LeftPanel, Library, type ManagerView } from "./components/LeftPanel";
import { AreaSwitcher } from "./components/AreaSwitcher";
import { ManagementModal } from "./components/ManagementModal";
import { BlogAdd, BlogView } from "./components/BlogView";
import { blogAddRequest } from "./lib/blogRequest";
import { BrandView } from "./components/BrandView";
import { SiteSettings } from "./components/SiteSettings";
import { addressValue, settingsSubtitle, type SettingsGroup } from "./lib/siteSettings";
import { fallbackPanelTab, panelTabs, type PanelTab } from "./lib/panelTabs";
import { Stax } from "@/components/stax/Stax";
import "./workspace.css";

export interface BuilderWorkspaceProps {
  siteId: string;
  api: BuilderApi;
  signInHref: string;
  /** The owner's card dashboard, when they also have a card site; the menu links to it. */
  cardSiteHref?: string;
  /** Signed-in account details supplied by the route. */
  account?: { email: string };
  /** Route that signs the owner out. */
  logoutHref?: string;
  /** Extra chrome from the host, e.g. the local demonstration label. */
  hostBanner?: ReactNode;
  navigationController?: WorkspaceNavigationController;
  onNavigationDirtyChange?: (dirty: boolean) => void;
}

const WIDE = "(min-width: 900px)";
/** Wide enough to dock the menu drawer beside the page. */
const DOCKED = "(min-width: 1200px)";
/** The Simple / Advanced switch is hidden for now; everyone works in Simple mode. */
const MODE_SWITCH: boolean = false;

async function croppedPhoto(src: string, crop: CropBox): Promise<Blob> {
  const image = new Image();
  if (new URL(src, window.location.href).origin !== window.location.origin) image.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error("Image unavailable")); image.src = src; });
  const source = cropToPixels(crop, image.naturalWidth, image.naturalHeight);
  const output = cropOutputSize(crop, image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement("canvas");
  canvas.width = output.width; canvas.height = output.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  // JPEG has no transparency: a logo's clear background (SVG or PNG) would otherwise turn black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(image, source.x, source.y, source.width, source.height, 0, 0, output.width, output.height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.86));
  if (!blob) throw new Error("Image unavailable");
  return blob;
}

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

/** The draft a review was prepared from. While reviewing, the canvas shows exactly that draft. */
function reviewedDraft(state: PublicationState): string | null {
  if (state.phase === "reviewing" || state.phase === "confirming" || state.phase === "confirm-unknown") {
    return state.action.review?.draftId ?? null;
  }
  if (state.phase === "blocked" && (state.reason === "stale" || state.reason === "refused")) {
    return state.action?.review?.draftId ?? null;
  }
  return null;
}

export function BuilderWorkspace({ siteId, api, signInHref, cardSiteHref, account, logoutHref, hostBanner, navigationController, onNavigationDirtyChange }: BuilderWorkspaceProps) {
  const { area, setArea, blogSection, setBlogSection, blogPostId, setBlogPostId, shopSection, setShopSection, shopProductId, setShopProductId, shopOrderId, setShopOrderId, pagePath, setPagePath } = useBuilderNavigation(navigationController);
  const [savedMode, setMode] = useWorkspaceMode(siteId);
  const mode = MODE_SWITCH ? savedMode : "simple";
  // Select in Advanced and Point to change in Simple are both pointer modes.
  const [pointer, setPointer] = useState(true);
  const { workspace, error, isLoading, isRefreshing, refresh } = useWorkspace(api, siteId);
  const publication = usePublication({ api, siteId, workspace, refreshWorkspace: refresh });
  // Null until the owner picks a page; until then the preview opens the draft's start page.
  const [navigation, setNavigation] = useState<{ path: string; count: number } | null>(null);
  const [currentPath, setCurrentPath] = useState<string | null>(pagePath);
  useEffect(() => {
    if (area !== "website") return;
    setNavigation(previous => pagePath ? { path: pagePath, count: (previous?.count ?? 0) + 1 } : null);
    setCurrentPath(pagePath);
  }, [area, pagePath]);
  const viewedPath = currentPath ?? navigation?.path ?? (workspace ? startPath(workspace.pages) : undefined);
  // Inspiration images for the next message; one that isn't accepted comes back above the message box.
  const references = useReferences({ api, siteId });
  const [shopSelectedOrder, setShopSelectedOrder] = useState<{ id: string; number: number } | null>(null);
  const openPostReader = useRef<(() => Promise<OpenPost | null>) | null>(null);
  const prepareOpenPost = useCallback(() => openPostReader.current?.() ?? Promise.resolve(null), []);
  const chat = useConversation({
    prepareOpenPost,
    api, siteId, workspaceDraftId: workspace?.site.draftId, refreshWorkspace: refresh,
    pagePath: workspace?.pages.some((page) => page.path === viewedPath) ? viewedPath : undefined,
    onAttachmentsReturned: references.restore,
    editorContext: area === "shop" ? { area, section: shopSection, ...(shopSection === "orders" && shopSelectedOrder ? { selectedId: shopSelectedOrder.id } : shopSection === "products" && shopProductId ? { selectedId: shopProductId } : {}) } : area === "blog" ? { area, section: blogSection, ...(blogPostId ? { selectedId: blogPostId } : {}) } : undefined,
  });
  const selectionRequest = usePostSelectionRequest(`${area}/${blogPostId ?? ""}`, (text, attachments, post) => {
    const sent = chat.send(text, attachments, post);
    if (sent) chat.setDraft("");
    return sent;
  });
  // The collapsed bubble says what Stax is doing while it replies.
  const working = useWorkingLabel(chat.conversation?.progress, chat.conversation?.status === "replying");
  const follower = useActionFollower({ api, siteId, workspace, refreshWorkspace: refresh });
  const comments = useComments({ api, siteId, chat });
  const refreshConversation = chat.refresh;
  const refreshAll = useCallback(async () => {
    const [fresh] = await Promise.all([refresh(), refreshConversation()]);
    return fresh;
  }, [refresh, refreshConversation]);

  const [panelOpen, setPanelOpen] = useState(() => typeof window !== "undefined" && window.matchMedia(WIDE).matches);
  const [panelTab, setPanelTab] = useState<PanelTab>(() => {
    try { return fallbackPanelTab(localStorage.getItem("bw-panel-tab")); } catch { return "chat"; }
  });
  // Back from checkout (?checkout=success|cancelled): reopen the review. The parameter is removed so
  // a reload doesn't repeat it.
  const [checkoutReturn] = useState<"success" | "cancelled" | null>(() => {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get("checkout");
    return value === "success" || value === "cancelled" ? value : null;
  });
  const removeSearchParameter = navigationController?.removeSearchParameter;
  useEffect(() => {
    if (!checkoutReturn) return;
    if (removeSearchParameter) {
      removeSearchParameter("checkout");
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    window.history.replaceState(window.history.state, "", url);
  }, [checkoutReturn, removeSearchParameter]);
  const reopened = useRef(false);
  // On a phone the chat covers the preview, so a reply that changed it closes the chat to show the
  // new preview, and the reply peeks above the bubble. Not while the owner is writing their next message.
  // On a phone the chat covers the preview, so it closes when there is something new to see there.
  const showPreviewOnPhone = useCallback(() => {
    if (window.matchMedia(WIDE).matches) return false;
    const writing = document.activeElement;
    if (writing instanceof HTMLTextAreaElement && writing.value.trim()) return false;
    setPanelOpen(false);
    return true;
  }, []);
  const peek = usePeek(chat.conversation?.messages, panelOpen, showPreviewOnPhone);
  // A first build's first look appears in the preview while Stax polishes it.
  const firstLookShown = Boolean(chat.conversation?.progress?.firstLook?.length);
  const firstLookSeen = useRef(firstLookShown);
  useEffect(() => {
    if (firstLookShown && !firstLookSeen.current) showPreviewOnPhone();
    firstLookSeen.current = firstLookShown;
  }, [firstLookShown, showPreviewOnPhone]);
  // The drawer is content management; saved changes and publishing live together in the right panel.
  const docked = useMedia(DOCKED);
  const dockedDrawer = docked && mode === "advanced";
  const [drawerOpen, setDrawerOpen] = useState(() => mode === "advanced" && typeof window !== "undefined" && window.matchMedia(DOCKED).matches);
  const [changesOpen, setChangesOpen] = useState(false);
  const [historyRequested, setHistoryRequested] = useState(false);
  useEffect(() => {
    if (!historyRequested || !changesOpen) return;
    document.getElementById("bw-earlier-versions")?.scrollIntoView({ block: "start" });
    setHistoryRequested(false);
  }, [historyRequested, changesOpen]);
  /** The change a preview marker pointed at, highlighted in the list. */
  const [focusedChange, setFocusedChange] = useState<string | null>(null);
  const [managerView, setManagerView] = useState<ManagerView | null>(null);
  /** The Site settings modal, with the group it opened at (none: Address on a wide screen, the list on a phone). */
  const [siteSettings, setSiteSettings] = useState<{ group?: SettingsGroup } | null>(null);
  const shopApi = useMemo(() => api.shop ?? createHttpShopApi(), [api]);
  const [shopProductShown, setShopProductShown] = useState<{ name: string; slug: string } | null>(null);
  const [blogPostTitle, setBlogPostTitle] = useState<string | null>(null);
  const [shopDirty, setShopDirty] = useState(false);
  const [blogDirty, setBlogDirty] = useState(false);
  useEffect(() => { onNavigationDirtyChange?.(shopDirty || blogDirty); }, [shopDirty, blogDirty, onNavigationDirtyChange]);
  const leaveShop = () => Boolean(navigationController) || !shopDirty || window.confirm("Discard unsaved shop changes?");
  const [shopAddOpen, setShopAddOpen] = useState(false);
  const shopAvailable = workspace ? siteHasFeature(workspace.site, "shop") : false;
  const shopRead = useResource(useCallback(signal => shopApi.getShop(siteId, signal), [shopApi, siteId]), siteId, shopAvailable);
  const shop = shopRead.data ?? null;
  const setShop = shopRead.replace;
  const readShop = shopRead.reload;
  const clearShop = shopRead.clear;
  const reloadShop = useCallback(async () => { await readShop(); }, [readShop]);
  // The site's areas: the Website, and the Blog once the site has one. The Blog's own screen and post are kept here so the drawer can change them.
  const blogOpen = area === "blog";
  const shopOpen = area === "shop";
  useEffect(() => { if (workspace && !shopAvailable) { clearShop(); setShopAddOpen(false); if (shopOpen) setArea("website", true); } }, [workspace, shopAvailable, shopOpen, clearShop, setArea]);
  const setBlogOpen = useCallback((open: boolean) => setArea(open ? "blog" : "website"), [setArea]);
  const [blogAddOpen, setBlogAddOpen] = useState(false);
  const [blogAddSuggestion, setBlogAddSuggestion] = useState<{ name?: string; base?: string } | null>(null);
  const [editingPost, setEditingPost] = useState(false);
  const [panelPost, setPanelPost] = useState(false);
  const setBlogEditing = useCallback((editing: boolean) => { setEditingPost(editing); if (editing && window.matchMedia(WIDE).matches) { setPanelPost(true); setPanelOpen(true); } else if (!editing) setPanelPost(false); }, []);
  // The platform turns the blog on per site. Without it there's nothing to load or open (the server refuses too).
  const blogAvailable = workspace ? siteHasFeature(workspace.site, "blog") : false;
  const blogRead = useResource(useCallback(signal => api.getBlog(siteId, signal), [api, siteId]), siteId, blogAvailable);
  const blog = blogRead.data ?? null;
  const readBlog = blogRead.reload;
  const clearBlog = blogRead.clear;
  const reloadBlog = useCallback(async () => { await readBlog(); }, [readBlog]);
  useEffect(() => { if (workspace && !blogAvailable) { if (area === "blog") setArea("website", true); setBlogAddOpen(false); } }, [workspace, blogAvailable, setArea, area]);
  // A blog that goes away (a site that loses it) can't stay open.
  useEffect(() => { if (area === "blog" && blog !== null && !blog.settings) setArea("website", true); }, [area, blog, setArea]);
  useEffect(() => { if (area === "shop" && shop !== null && !shop.settings) setArea("website", true); }, [area, shop, setArea]);
  const websiteNotLive = workspace ? unpublishedChanges(workspace).filter((row) => !row.undone).length : 0;
  const siteFeatures = (workspace?.site.features ?? []).join(",");
  const blogName = blogAvailable && blog?.settings ? blog.settings.name : null;
  const blogEnabled = blog?.settings?.enabled ?? false;
  const chatReady = chat.conversation?.status === "ready";
  const chatLastMessage = chat.conversation?.messages.at(-1)?.id;
  useEffect(() => { if (chatReady && !shopDirty) void reloadShop().catch(() => {}); }, [chatReady, chatLastMessage, shopDirty, reloadShop]);
  useEffect(() => { if (chatReady) void reloadBlog().catch(() => {}); }, [chatReady, chatLastMessage, reloadBlog]);
  const areaRows = useMemo(() => switcherRows({ site: { features: siteFeatures ? siteFeatures.split(",") : [] }, blog: blogName === null ? null : { name: blogName, enabled: blogEnabled }, shop: shopAvailable ? shop?.settings : null, websiteNotLive, current: area }), [siteFeatures, blogName, blogEnabled, websiteNotLive, area, shopAvailable, shop?.settings]);
  const switchAreaRef = useRef<(next: Area) => void>(() => undefined);
  useEffect(() => {
    if (!areaRows.visible) return;
    const onKey = (event: KeyboardEvent) => {
      const next = areaForShortcut(event, areaRows.areas);
      if (!next) return;
      event.preventDefault();
      switchAreaRef.current(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [areaRows.visible, areaRows.areas]);
  // A reply from Stax can change a post (tags, a photo description): take it in once the turn is done.
  const turnPending = chat.conversation?.pendingTurnId ?? null;
  const wasPending = useRef(turnPending);
  const hasBlog = Boolean(blog?.settings);
  useEffect(() => {
    if (wasPending.current !== null && turnPending === null && hasBlog) void reloadBlog().catch(() => { /* Keep the last private view while reconnecting. */ });
    wasPending.current = turnPending;
  }, [turnPending, hasBlog, reloadBlog]);
  useEffect(() => { if (workspace?.site.draftId) void reloadBlog().catch(() => { /* Keep the last private view while reconnecting. */ }); }, [workspace?.site.draftId, reloadBlog]);
  useEffect(() => {
    if (!dockedDrawer) setDrawerOpen(false);
  }, [dockedDrawer]);
  // Editing text on the page. Saved text is written straight into the preview; the frame keeps
  // showing the draft it loaded (no reload) until the owner moves to another page.
  const [picked, setPicked] = useState<TextPick | null>(null);
  const [selection, setSelection] = useState<SectionSelection | null>(null);
  const [sections, setSections] = useState<SectionSummary[] | null>(null);
  const [previewPageTitles, setPreviewPageTitles] = useState<Record<string, string>>({});
  const [insertAfter, setInsertAfter] = useState<SectionSummary | null>(null);
  const [photoPick, setPhotoPick] = useState<PhotoPick | null>(null);
  const [replacementTarget, setReplacementTarget] = useState<PhotoPick | null>(null);
  const [photoAction, setPhotoAction] = useState<"" | "replace" | "crop">("");
  const [photoStatus, setPhotoStatus] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  // One-time hints for Point to change (Simple) and Select (Advanced), remembered once dismissed.
  const [hintsDismissed, setHintsDismissed] = useState(() => {
    const read = (key: string) => { try { return localStorage.getItem(key) === "1"; } catch { return false; } };
    return { point: read("bw-hint-point"), select: read("bw-hint-select") };
  });
  useEffect(() => { setPointer(true); setCommenting(false); }, [mode]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [textSave, setTextSave] = useState<"idle" | "saving" | "failed">("idle");
  const [textUndo, setTextUndo] = useState<{ bind: string; label: string; before: string; after: string } | null>(null);
  // A list item changed from its own fields; the page reloads and its card closes, so the toast says it saved.
  const [itemSaved, setItemSaved] = useState<string | null>(null);
  useEffect(() => {
    if (!itemSaved) return;
    const timer = setTimeout(() => setItemSaved(null), 6_000);
    return () => clearTimeout(timer);
  }, [itemSaved]);
  // Comments on the page: the pin being written, the pin whose card is open, and a pin to bring into view.
  const [commenting, setCommenting] = useState(false);
  const [newPin, setNewPin] = useState<CommentDrop | null>(null);
  const [commentPrefill, setCommentPrefill] = useState("");
  const [openPin, setOpenPin] = useState<{ id: string; at: FrameAnchor } | null>(null);
  const [reveal, setReveal] = useState<{ id: string; nonce: number } | null>(null);
  // Point to change: step back up to a level above what was selected (the section, or the card).
  const [pointStep, setPointStep] = useState<{ depth: number; nonce: number } | null>(null);
  const wide = useMedia(WIDE);
  // The site as visitors see it, at desktop, tablet and phone sizes, over the editor.
  const [previewing, setPreviewing] = useState(false);
  // Touch screens (phones, tablets) tap rather than click.
  const touchScreen = useMedia("(hover: none)");
  useEffect(() => {
    if (mode === "simple") { setPanelTab("chat"); return; }
    if (selection) {
      setPanelTab("section");
      if (wide) setPanelOpen(true);
    } else {
      setPanelTab((current) => {
        if (current !== "section") return current;
        try { return fallbackPanelTab(localStorage.getItem("bw-panel-tab")); } catch { return "chat"; }
      });
    }
  }, [mode, selection, wide]);
  const [frameDraftId, setFrameDraftId] = useState<string | null>(null);
  // A card is placed where its pin was on the page on screen; another page or a reload closes it.
  useEffect(() => {
    setNewPin(null);
    setOpenPin(null);
    setSelection(null);
    setPhotoPick(null);
    setSections(null);
    setInsertAfter(null);
  }, [currentPath, navigation?.path, frameDraftId]);
  useEffect(() => {
    if (mode === "advanced" && pointer && !commenting) return;
    setSelection(null);
    setSections(null);
  }, [mode, pointer, commenting]);
  const silentDrafts = useRef(new Set<string>());
  // An earlier version shown on the canvas from History; the draft itself is unchanged.
  const [versionPreview, setVersionPreview] = useState<HistoryEntry | null>(null);
  const [restoring, setRestoring] = useState<"idle" | "working" | "failed">("idle");
  const latestDraft = workspace?.site.draftId;
  useEffect(() => {
    setVersionPreview(null);
    setRestoring("idle");
    if (!latestDraft) return;
    // A draft made by saving text on the page is already on screen; anything else reloads it.
    if (silentDrafts.current.has(latestDraft)) return;
    setFrameDraftId(latestDraft);
    setOverrides({});
  }, [latestDraft]);
  useEffect(() => {
    if (!textUndo) return;
    const timer = setTimeout(() => setTextUndo(null), 15_000);
    return () => clearTimeout(timer);
  }, [textUndo]);
  // Photos the owner chose, from the chat or the Photos modal, waiting for their descriptions.
  const [pendingPhotos, setPendingPhotos] = useState<{ files: File[]; from: "chat" | "content" } | null>(null);
  const library = useLibrary({ api, siteId, workspace, enabled: drawerOpen || managerView === "content" || siteSettings !== null || Boolean(workspace && (workspace.pages.some(isRoutedOwnerPage) || viewedPath === NOT_FOUND_PREVIEW_PATH) && pointer) || Boolean(photoPick), refresh: refreshAll });
  const undo = useUndo({ api, siteId, workspace, refresh: refreshAll });
  // A site without pages starts in conversation, so the panel opens on every screen size.
  const empty = workspace ? !workspace.pages.some(isRoutedOwnerPage) : false;
  useEffect(() => {
    if (empty) setPanelOpen(true);
  }, [empty]);
  const bubble = useRef<HTMLButtonElement>(null);
  const keyboardRoot = useKeyboardLayout();
  const wasOpen = useRef(panelOpen);

  const { state } = publication;
  const blockedAccess = state.phase === "blocked" && (state.reason === "signed-out" || state.reason === "not-found") ? state.reason : null;
  const access = workspaceAccess({
    hasData: Boolean(workspace),
    isLoading,
    error,
    otherFailure: blockedAccess ?? follower.failure ?? chat.failure,
  });
  const disconnected = access.kind === "disconnected";

  // However access ended, the private conversation is dropped, not just hidden.
  const revokedBy = access.kind === "revoked" ? access.failure : null;
  useEffect(() => { if (revokedBy) { clearBlog(); setBlogOpen(false); setBlogAddOpen(false); } }, [revokedBy, setBlogOpen, clearBlog]);
  const forgetChat = chat.forget;
  useEffect(() => {
    if (revokedBy) forgetChat(revokedBy);
  }, [revokedBy, forgetChat]);

  // Starting or following a publication brings the panel forward.
  const flowActive = state.phase !== "idle";
  // The Live button's panel: visit, copy, domain, earlier versions. Only while nothing is being published.
  const [liveOpen, setLiveOpen] = useState(false);
  // Back to Stax while a publish is still going: the chat shows, and the publish carries on.
  const [publishHidden, setPublishHidden] = useState(false);
  const publishing = usePublishing({ api, siteId, enabled: flowActive || liveOpen || drawerOpen || siteSettings !== null, awaitingPlan: flowActive && checkoutReturn === "success" });
  const dismissPublication = publication.dismiss;
  useEffect(() => {
    if (state.phase !== "idle") setLiveOpen(false);
    if (state.phase === "preparing") setPublishHidden(false);
    // It finished while the owner was elsewhere: the top bar and its line now say how it went.
    if (state.phase === "settled" && publishHidden) {
      setPublishHidden(false);
      dismissPublication();
    }
  }, [state.phase, publishHidden, dismissPublication]);
  const serving = useServingChange({ api, siteId, workspace, refreshWorkspace: refreshAll, onChanged: publishing.reload });
  const startPublication = publication.start;
  const workspaceReady = Boolean(workspace);
  useEffect(() => {
    if (!checkoutReturn || reopened.current || !workspaceReady || state.phase !== "idle") return;
    reopened.current = true;
    startPublication();
  }, [checkoutReturn, workspaceReady, state.phase, startPublication]);
  useEffect(() => {
    if (flowActive) setPanelOpen(true);
  }, [flowActive]);

  // Return focus to the bubble when the panel is hidden.
  useEffect(() => {
    if (wasOpen.current && !panelOpen) bubble.current?.focus({ preventScroll: true });
    wasOpen.current = panelOpen;
  }, [panelOpen]);

  const address = workspace ? displayOrigin(workspace.manifest.canonicalOrigin) : "";
  const siteName = workspace?.siteName ?? null;
  useEffect(() => {
    if (!address) return;
    const previous = document.title;
    document.title = `${siteName ?? address} – StackJe`;
    return () => {
      document.title = previous;
    };
  }, [address, siteName]);

  if (access.kind === "loading") {
    return (
      <div className="bw" data-panel="open" aria-busy="true">
        <span className="bw-sr" role="status">Opening your website…</span>
        <div className="bw-bar" aria-hidden="true">
          <div className="bw-identity">
            <Skeleton width={28} height={28} radius={999} />
            <Skeleton width={140} height={16} radius={4} />
          </div>
          <Skeleton width={96} height={44} radius={6} />
        </div>
        <div className="bw-skeleton-panel" aria-hidden="true">
          <Skeleton width="70%" height={14} radius={4} />
          <Skeleton width="90%" height={14} radius={4} />
          <Skeleton width="55%" height={14} radius={4} />
          <Skeleton width="80%" height={14} radius={4} />
        </div>
        <main className="bw-stage" aria-hidden="true">
          <div className="bw-frame">
            <PageOutlineSkeleton label="Opening your website" />
          </div>
        </main>
      </div>
    );
  }

  if (access.kind === "revoked" || access.kind === "unavailable" || !workspace) {
    return (
      <div className="bw">
        <WorkspaceProblem
          access={access.kind === "revoked" || access.kind === "unavailable" ? access : { kind: "unavailable", failure: "unavailable" }}
          signInHref={signInHref}
          onRetry={() => void refresh()}
          retrying={isRefreshing}
        />
      </div>
    );
  }

  const { site, pages } = workspace;
  const hasRoutedPages = pages.some(isRoutedOwnerPage);
  const hasPreviewPage = hasRoutedPages || viewedPath === NOT_FOUND_PREVIEW_PATH;
  const waiting = state.phase === "idle" ? waitingReview(workspace) : null;
  const changeRows = unpublishedChanges(workspace);
  const notLive = unpublishedLabel(changeRows);
  const status = state.phase === "publishing"
    ? { label: "Publishing…", tone: "progress" as Tone }
    : notLive ? { label: `Saved · ${notLive}`, tone: "attention" as Tone } : describeLiveStatus(liveStatus(workspace));
  const pinned = reviewedDraft(state);
  // A publish review pins its draft; otherwise a version from History; otherwise the latest draft.
  const versionShown = pinned === null && versionPreview?.draftId ? versionPreview : null;
  const canvasRoot = pinned !== null
    ? releasePreviewRoot(site.id, pinned)
    : draftPreviewRoot(site.id, versionShown?.draftId ?? frameDraftId ?? site.draftId);
  // Text can be edited only on the owner's own latest draft.
  const canEdit = pinned === null && !versionShown && hasPreviewPage;
  const editingNow = pointer && canEdit && mode === "advanced" && !commenting;
  const pointingNow = pointer && canEdit && mode === "simple";
  const commentingNow = commenting && canEdit && mode === "advanced";
  const openPanel = () => setPanelOpen(true);
  const openChanges = () => {
    setChangesOpen(true);
    setPanelOpen(true);
  };
  const home = startPath(pages);
  // A page the owner chose can disappear (removed, or undone); the canvas then returns home.
  const requestedPath = navigation && pages.some((page) => page.path === navigation.path) ? navigation.path : home;
  const shownPath = currentPath ?? requestedPath;
  const catchUpFrame = () => {
    if (frameDraftId !== site.draftId) {
      setFrameDraftId(site.draftId);
      setOverrides({});
    }
  };
  const saveText = async (pick: TextPick, text: string, isUndo = false, onlyHere = false) => {
    const [recordId, field] = pick.bind.split(".") as [string, string];
    const matches = pick.matches?.length ? pick.matches : [{ bind: pick.bind, bindingId: pick.bindingId ?? "" }];
    const uniqueBinds = [...new Set(matches.map((match) => match.bind))];
    const changedBinds = onlyHere ? [pick.bind] : uniqueBinds;
    setOverrides((now) => ({ ...now, ...Object.fromEntries(changedBinds.map((bind) => [bind, text])) }));
    setTextSave("saving");
    try {
      const command: OwnerCommand = onlyHere && pick.bindingId
        ? { kind: "content.patch-instance", bindingId: pick.bindingId, recordId, field, text, ...(pick.nodeId ? { nodeId: pick.nodeId } : {}) }
        : uniqueBinds.length > 1
          ? { kind: "content.edit", edits: uniqueBinds.map((bind) => {
            const [targetRecordId, targetField] = bind.split(".") as [string, string];
            return { recordId: targetRecordId, field: targetField, text };
          }) }
          : { kind: "content.patch", recordId, set: { [field]: text } };
      const action = await api.submitAction(siteId, { actionId: `text-${Date.now().toString(36)}`, baseDraftId: site.draftId, command });
      if (!["queued", "running", "succeeded"].includes(action.state)) throw new Error("Not saved");
      const draftId = typeof action.result?.draftId === "string" ? action.result.draftId : null;
      if (draftId) silentDrafts.current.add(draftId);
      setTextSave("idle");
      setPicked(null);
      setTextUndo(isUndo || command.kind !== "content.patch" ? null : { bind: pick.bind, label: FIELDS[field] ?? "Text", before: pick.text, after: text });
      await refreshAll();
    } catch {
      setOverrides((now) => ({ ...now, ...Object.fromEntries(changedBinds.map((bind) => [bind, pick.text])) }));
      setTextSave("failed");
    }
  };
  /** Saves an Instant change against the current draft; false when it wasn't applied. */
  const submitInstant = async (prefix: string, command: OwnerCommand) => {
    try {
      const action = await api.submitAction(siteId, { actionId: `${prefix}-${Date.now().toString(36)}`, baseDraftId: site.draftId, command });
      if (!["queued", "running", "succeeded"].includes(action.state)) throw new Error("Not saved");
      await refreshAll();
      return true;
    } catch { return false; }
  };
  /** Brings back the design or brand from before a change (the server holds a design restore for confirmation). */
  const restorePart = async (entry: HistoryEntry, part: RestorePart): Promise<boolean> => {
    if (!entry.baseDraftId) return false;
    const command: OwnerCommand = part === "design" ? { kind: "design.restore", fromDraftId: entry.baseDraftId } : { kind: "brand.restore", fromDraftId: entry.baseDraftId };
    const done = await submitConfirmed(api, siteId, site.draftId, `restore-${part}`, command);
    if (done) await refreshAll();
    return done;
  };
  const setSectionVisibility = (section: SectionSummary, hidden: boolean) =>
    submitInstant("section", { kind: "section.hide", pagePath: shownPath, nodeId: section.nodeId, hidden });
  const selectPage = (path: string) => {
    setPagePath(path);
    catchUpFrame();
    setNavigation((previous) => ({ path, count: (previous?.count ?? 0) + 1 }));
    setCurrentPath(path);
  };
  const canPreview = hasPreviewPage;
  const closePreview = (path: string) => {
    setPreviewing(false);
    // Back on the page the preview ended on, when it's one of the site's own.
    if (path !== shownPath && pages.some((page) => page.path === path)) selectPage(path);
  };
  const changeCount = changeRows.filter((row) => !row.undone).length;
  const openPublish = () => {
    setPublishHidden(false);
    setPanelOpen(true);
  };
  const publish = publishControl(workspace, state, { disconnected, versionBlocked: versionShown !== null, changeCount }, {
    start: publication.start,
    resumeWaiting: waiting ? () => publication.resume(waiting) : null,
    openPanel: openPublish,
    openLive: () => {
      setLiveOpen(true);
      setPanelOpen(true);
    },
    retry: publication.reviewAgain,
  });
  const statusLine = publishStatusLine(workspace, state, changeCount, { websiteOnly: Boolean(blog?.settings || shop?.settings) });
  // Leaving the publish panel keeps a publish that's under way going; anything else is let go.
  const leavePublish = () => {
    setLiveOpen(false);
    if (["publishing", "confirming", "confirm-unknown"].includes(state.phase)) setPublishHidden(true);
    else publication.dismiss();
  };
  const leaveTo = (next: () => void) => () => {
    leavePublish();
    next();
  };
  const publishView = flowActive && !publishHidden
    ? publishPanel({
      publication,
      publishing,
      workspace,
      disconnected,
      checkoutReturn,
      onAddressChanged: publication.reviewAgain,
      onOpenDomains: leaveTo(() => openSiteSettings("address")),
      onOpenHistory: leaveTo(() => openHistory()),
      changes: changeRows,
      onManageChanges: leaveTo(() => openChanges()),
      onAskStax: (text) => leaveTo(() => askAdmin(text))(),
      hasBlog: Boolean(blog?.settings),
    })
    : liveOpen
      ? livePanel({ workspace, publishing, serving, onOpenDomains: leaveTo(() => openSiteSettings("address")), onOpenHistory: leaveTo(() => openHistory()) })
      : null;
  const currentPage = pages.find((page) => page.path === shownPath) ?? pages.find((page) => page.path === home);
  // On a phone or narrow window the drawer covers the page, so it steps aside once the owner acts.
  const leaveDrawer = () => {
    if (!dockedDrawer) setDrawerOpen(false);
  };
  const openManager = (view: ManagerView) => {
    leaveDrawer();
    setManagerView(view);
  };
  const openSiteSettings = (group?: SettingsGroup) => {
    leaveDrawer();
    setSiteSettings({ group });
  };
  // History lives in Changes, under the unpublished changes; open it there, scrolled to it.
  const openHistory = () => {
    leaveDrawer();
    openChanges();
    setHistoryRequested(true);
  };
  /** The panels Stax's buttons point to. Stax never publishes or changes the address itself, so these open what the owner does. */
  const openStaxPanel = (panel: OpenPanel, suggestion?: { name?: string; base?: string }) => {
    if (panel === "blog") { if (!blogAvailable) return; if (blog?.settings) switchArea("blog"); else { leaveDrawer(); setBlogAddSuggestion(suggestion ?? null); setBlogAddOpen(true); } }
    else if (panel === "publish") publish.onClick();
    else if (panel === "live") {
      if (workspace.site.activeReleaseId && state.phase === "idle") { setLiveOpen(true); setPanelOpen(true); }
      else publish.onClick();
    } else if (panel === "domain") openSiteSettings("address");
    else if (panel === "site-details") openSiteSettings("search");
    else if (panel === "history") openHistory();
    else openManager(panel);
  };
  const toChat = (draft?: string) => {
    leaveDrawer();
    setPanelOpen(true);
    setPanelTab("chat");
    if (draft !== undefined) {
      chat.setDraft(draft);
      requestAnimationFrame(() => document.getElementById("bw-message")?.focus());
    }
  };
  const askAdmin = (text: string) => {
    toChat();
    chat.send(text);
  };
  /** Moves between the site's areas; the drawer and Ctrl+1 / Ctrl+2 both come here. */
  const switchArea = (next: Area) => {
    if (next !== area && !leaveShop()) return;
    if (next === "shop" && !(shopAvailable && shop?.settings)) return;
    if (next === "shop" && !wide) setPanelOpen(false);
    if (next === "shop") setPanelPost(false);
    if (next === "blog" && !(blogAvailable && blog?.settings)) return;
    leaveDrawer();
    if (next === "blog") {
      setBlogPostId(null);
      if (!wide) setPanelOpen(false);
    }
    setArea(next);
  };
  switchAreaRef.current = switchArea;
  const openAddBlog = () => { leaveDrawer(); setBlogAddSuggestion(null); setBlogAddOpen(true); };
  /** The blog's pages (its list, post and tag pages) are designed in the Website, with Stax. */
  const designBlogPages = () => {
    leaveDrawer();
    setArea("website");
    const base = blog?.settings?.base;
    if (base && pages.some((page) => page.path === base)) selectPage(base);
  };
  const designShopPages = () => {
    if (!leaveShop()) return;
    leaveDrawer();
    setArea("website");
    const base = shop?.settings?.base;
    if (base && pages.some((page) => page.path === base)) selectPage(base);
  };
  const photoAdd = pendingPhotos && workspace ? (
    <PhotoAdd
      key={pendingPhotos.files.map((file) => file.name).join("|")}
      api={api}
      siteId={siteId}
      workspace={workspace}
      files={pendingPhotos.files}
      refresh={async () => { await Promise.all([refreshAll(), library.retry()]); }}
      onCancel={() => setPendingPhotos(null)}
      onAdded={(records) => {
        const from = pendingPhotos.from;
        setPendingPhotos(null);
        // From the chat, the natural next step is asking the admin to use them; the owner sends it.
        if (from === "chat") {
          chat.setDraft(`I’ve added ${records.length === 1 ? "a photo" : `${records.length} photos`}: ${records.map((record) => record.alt).join("; ")}. Please use ${records.length === 1 ? "it" : "them"} on my website.`);
          requestAnimationFrame(() => document.getElementById("bw-message")?.focus());
        } else if (replacementTarget && records[0]) {
          // Adding the photo moved the draft on, so replace against the draft it produced.
          void swapPhoto(replacementTarget, records[0].id, records[0].alt || "I just added", async () => (await api.getWorkspace(siteId)).site.draftId)
            .finally(() => setReplacementTarget(null));
        }
      }}
    />
  ) : null;
  const saveBrand = async (command: Extract<OwnerCommand, { kind: "brand.set" }>) => {
    try {
      const action = await api.submitAction(siteId, { actionId: `brand-${Date.now().toString(36)}`, baseDraftId: workspace.site.draftId, command });
      await refreshAll();
      return ["queued", "running", "succeeded"].includes(action.state);
    } catch {
      return false;
    }
  };
  const saveSiteDetails = async (command: Extract<OwnerCommand, { kind: "site.set" }>) => {
    try {
      const action = await api.submitAction(siteId, { actionId: `site-${Date.now().toString(36)}`, baseDraftId: workspace.site.draftId, command });
      await refreshAll();
      return ["queued", "running", "succeeded"].includes(action.state);
    } catch {
      return false;
    }
  };
  const closeComment = () => {
    setNewPin(null);
    setOpenPin(null);
    setCommentPrefill("");
  };
  const openPhotoComment = (note: string) => {
    if (photoPick?.nodeId) setNewPin({ nodeId: photoPick.nodeId, ...(photoPick.itemId ? { itemId: photoPick.itemId } : {}), x: photoPick.x, y: photoPick.y, viewport: wide ? "desktop" : "phone", top: photoPick.bottom, bottom: photoPick.bottom, left: photoPick.left, scrollY: photoPick.scrollY });
    setPhotoPick(null);
    setPhotoAction("");
    setCommentPrefill(note);
  };
  /**
   * Shows `sourceRecordId` in the picked photo's place straight away: a bound photo takes the new
   * picture, an image the design didn't bind is bound to it, and an item with no photo gets it.
   * Anything else is asked of Stax.
   */
  const swapPhoto = async (target: PhotoPick, sourceRecordId: string, label: string, baseDraftId: () => Promise<string>) => {
    const command: OwnerCommand | null = target.recordId
      ? { kind: "media.replace", recordId: target.recordId, sourceRecordId }
      : target.unbound
        ? { kind: "media.bind", pagePath: target.unbound.path, src: target.unbound.src, occurrence: target.unbound.occurrence, recordId: sourceRecordId, ...(target.nodeId ? { nodeId: target.nodeId } : {}) }
        : target.slot
          ? { kind: "content.patch", recordId: target.slot, set: { image: sourceRecordId } }
          : null;
    if (!command) { openPhotoComment(`Use the photo “${label}” here`); return; }
    setPhotoStatus({ busy: true, error: null });
    try {
      const action = await api.submitAction(siteId, { actionId: `replace-${Date.now().toString(36)}`, baseDraftId: await baseDraftId(), command });
      if (!["queued", "running", "succeeded"].includes(action.state)) throw new Error("Not saved");
      await Promise.all([refreshAll(), library.retry()]);
      setPhotoPick(null); setPhotoAction("");
    } catch { setPhotoStatus({ busy: false, error: target.slot ? "The photo wasn’t added. Try again." : "The photo wasn’t replaced. Try again." }); }
  };
  // Pins show on the owner's own draft; a pin whose part of the page is gone waits at the top of the preview.
  const showPins = canEdit;
  const pins: PinView[] = showPins ? comments.comments
    .filter((comment) => comment.pagePath === shownPath)
    .map((comment) => ({
      id: comment.id, number: comment.number, nodeId: comment.nodeId, ...(comment.itemId ? { itemId: comment.itemId } : {}), x: comment.x, y: comment.y,
      state: comment.state, label: pinLabel(comment, comments.moved.has(comment.id)), active: openPin?.id === comment.id, moved: comments.moved.has(comment.id),
    })) : [];
  // A note on a section or card Point to change selected needs no pin: its outline shows what it is about, and a pin would block clicking further in.
  if (showPins && newPin && !newPin.path) pins.push({ id: "new-comment", number: null, nodeId: newPin.nodeId, ...(newPin.itemId ? { itemId: newPin.itemId } : {}), x: newPin.x, y: newPin.y, state: "new", label: "New comment", active: true });
  // While Changes is open on the draft, each placed change gets a numbered marker on its section.
  const shownFile = bundlePageFile(workspace.manifest.routes, shownPath);
  const markers = changesOpen && versionShown === null ? changeMarkers(changeRows, sections ?? [], shownFile) : [];
  pins.push(...markers.map((marker) => ({ id: `change-${marker.actionId}`, number: marker.number, nodeId: marker.nodeId, x: 0, y: 0, state: "done" as const, label: `Change ${marker.number}` })));
  const openComment = openPin ? comments.comments.find((comment) => comment.id === openPin.id) ?? null : null;
  const pageTitles = outlinePageTitles(pages, {
    areas: { blog: blog?.settings, shop: shop?.settings },
    stored: workspace.seo?.pages,
    preview: previewPageTitles,
  });
  const showComment = (comment: { id: string; pagePath: string }) => {
    if (comment.pagePath !== shownPath && pages.some((page) => page.path === comment.pagePath)) selectPage(comment.pagePath);
    setVersionPreview(null);
    setPointer(false);
    setPicked(null);
    closeComment();
    // On a phone the panel covers the page.
    if (!wide) setPanelOpen(false);
    setReveal({ id: comment.id, nonce: Date.now() });
  };
  // Point to change heads each popover with what was selected, from the section down.
  const trailFor = (path: PointPath | undefined, id: string) => path
    ? <PointTrail path={path} id={id} onStep={(depth) => setPointStep({ depth, nonce: Date.now() })} />
    : undefined;
  const selectedStep = newPin?.path?.steps[newPin.path.depth];
  const noteDocked = selectedStep?.level === "section" || selectedStep?.level === "item";
  // A selected list item offers its own photo, words and link to change exactly, above the note to Stax.
  const selectedItem = selectedStep?.level === "item" ? itemRecord(newPin?.records, library.library?.entries) : null;
  const libraryPhotos = library.library?.entries.flatMap((entry) => entry.kind === "media" && entry.src ? [{ id: entry.id, label: entry.label, src: entry.src }] : []) ?? [];
  const commentCard = showPins && (newPin || (openPin && openComment)) ? (
    <CommentCard
      key={openPin?.id ?? "new"}
      trail={newPin ? trailFor(newPin.path, "bw-comment-title") : undefined}
      about={selectedStep ? stepName(selectedStep, FIELDS) : undefined}
      docked={noteDocked}
      fields={selectedItem ? <ItemFields entry={selectedItem} photos={libraryPhotos} onSave={async (set) => {
        const saved = await submitInstant("item", { kind: "content.patch", recordId: selectedItem.id, set });
        if (saved) {
          setItemSaved(`${typeof set.title === "string" ? set.title : selectedItem.label} saved to your draft. Undo it in Changes.`);
          await library.retry();
        }
        return saved;
      }} /> : undefined}
      anchor={newPin ?? openPin!.at}
      comment={newPin ? null : openComment}
      moved={openComment ? comments.moved.has(openComment.id) : false}
      saving={comments.busy === "saving"}
      error={comments.notice === "unsaved" ? "That didn’t save. Try again." : comments.notice === "full" ? "You have as many open comments as you can. Send or remove some first." : null}
      initialNote={commentPrefill}
      onClose={closeComment}
      onSave={async (note) => {
        if (newPin) {
          const { top: _top, bottom: _bottom, left: _left, scrollY: _scrollY, path: _path, records: _records, ...anchor } = newPin;
          if (await comments.create({ id: newCommentId(), pagePath: shownPath, ...anchor, note })) setNewPin(null);
        } else if (openComment && await comments.update(openComment.id, note)) setOpenPin(null);
      }}
      onRemove={openComment ? async () => { if (await comments.remove(openComment.id)) setOpenPin(null); } : undefined}
    />
  ) : null;
  const restoreCommentVersion = () => {
    const entry = historyEntries(workspace).find((candidate) => candidate.kind === "version") ?? null;
    if (entry) setVersionPreview(entry);
    openChanges();
  };
  const compactTray = canEdit && mode === "simple" ? (
    <CommentTray comments={comments} pageTitles={pageTitles} currentPath={shownPath} canSend={chat.ready && chat.conversation?.status === "ready"} placing={commentingNow} onShow={showComment} />
  ) : null;
  const requestAtSection = async (note: string, request: NonNullable<NewComment["request"]>): Promise<boolean> => {
    if (!selection) return false;
    const drop = dropAt(selection, wide ? "desktop" : "phone");
    return comments.create({ id: newCommentId(), pagePath: shownPath, nodeId: drop.nodeId, ...(drop.itemId ? { itemId: drop.itemId } : {}), x: drop.x, y: drop.y, viewport: drop.viewport, note, request });
  };
  const selectPanelTab = (tab: PanelTab) => {
    setPanelTab(tab);
    if (tab !== "section") {
      try { localStorage.setItem("bw-panel-tab", tab); } catch { /* storage may be unavailable */ }
    }
  };
  const currentSection = selection ? sections?.find((section) => section.nodeId === selection.nodeId) ?? selection : null;
  const availableTabs = panelTabs(mode, Boolean(currentSection));
  const selectedPanelTab = availableTabs.includes(panelTab) ? panelTab : fallbackPanelTab(null);

  // Where else the picked text appears, from the content library.
  const textUsage = (pick: TextPick): { note: string | null; multiple: boolean } => {
    const bind = pick.bind;
    const entry = library.library?.entries.find((candidate) => candidate.id === bind.split(".")[0]);
    const repeatedHere = (pick.matches?.length ?? 0) > 1;
    if (!entry) return { note: repeatedHere ? "This wording also appears elsewhere on this page." : null, multiple: repeatedHere };
    const elsewhere = entry.usedOn.filter((page) => page.path !== shownPath).map((page) => page.title);
    if (repeatedHere) return { note: "This wording also appears elsewhere on this page.", multiple: true };
    if (elsewhere.length > 0) return { note: `Also changes it on ${elsewhere.length === 1 ? elsewhere[0] : `${elsewhere.slice(0, -1).join(", ")} and ${elsewhere.at(-1)}`}.`, multiple: true };
    if (entry.shared) return { note: "It also appears on every page (menu, header or footer).", multiple: true };
    return { note: "It only appears here.", multiple: false };
  };
  // A change waiting for the owner's go-ahead needs them more than anything but a publication.
  const signal = state.phase === "idle" && chat.conversation?.confirmation ? { label: "Needs your OK", tone: "attention" as Tone } : bubbleLabel(state, Boolean(waiting));
  // Comments wait on the page until they're sent; the bubble counts them so the owner knows to open it and send.
  const unsent = canEdit ? comments.drafts.length : 0;
  // "Ask Stax" gives way to what Stax is doing, then to comments waiting to be sent; any other signal (a review, publishing) matters more.
  const bubbleState = signal.tone === null && working ? { label: working, tone: "progress" as Tone }
    : signal.tone === null && unsent > 0 ? { label: unsent === 1 ? "Send 1 comment" : `Send ${unsent} comments`, tone: "attention" as Tone }
    : signal;
  const mood = staxMood(state, bubbleState.tone, { replying: chat.conversation?.status === "replying", failed: Boolean(chat.failure) || chat.conversation?.status === "unavailable" });

  let reviewBanner: ReactNode = null;
  if (pinned) {
    reviewBanner = (
      <div className="bw-strip" data-tone="attention">
        <span>{pinned === site.draftId ? "You’re reviewing what will go live." : "You’re viewing the version you reviewed, not your latest changes."}</span>
      </div>
    );
  } else if (versionShown) {
    reviewBanner = (
      <div className="bw-strip bw-strip--version" data-tone="proposal" role="status">
        <span><strong>{versionShown.title}</strong> · {versionShown.day} {versionShown.detail.split(" · ")[0]}. Your draft hasn’t changed.</span>
        <span className="bw-strip-actions">
          <button
            type="button"
            className="bw-btn bw-btn--quiet bw-btn--on-dark"
            disabled={restoring === "working" || disconnected}
            onClick={async () => {
              setRestoring("working");
              try {
                const action = await api.submitAction(siteId, { actionId: `restore-${Date.now().toString(36)}`, baseDraftId: site.draftId, command: { kind: "draft.restore", fromDraftId: versionShown.draftId! } });
                if (!["queued", "running", "succeeded"].includes(action.state)) throw new Error("Not restored");
                await refreshAll();
              } catch {
                setRestoring("failed");
              }
            }}
          >
            {restoring === "working" && <Spinner />}
            {restoring === "working" ? "Restoring…" : "Restore this version"}
          </button>
          <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setVersionPreview(null)}>Back to latest</button>
        </span>
        {restoring === "failed" && <span role="alert">That version couldn’t be restored. Nothing was changed.</span>}
      </div>
    );
  }

  const pickedEntry = photoPick?.recordId ? library.library?.entries.find((entry) => entry.id === photoPick.recordId) : undefined;
  const photoActions = photoPick ? (
    <PhotoActions
      wide={wide}
      top={Math.max(8, photoPick.bottom + 10)}
      left={Math.max(8, photoPick.left)}
      recordId={photoPick.recordId}
      replaceable={Boolean(photoPick.recordId || photoPick.unbound || photoPick.slot)}
      empty={Boolean(photoPick.slot)}
      alt={photoPick.alt}
      src={pickedEntry?.src ?? photoPick.src}
      pageTitle={[currentPage?.title ?? "Home", sections?.find((section) => section.nodeId === photoPick.nodeId)?.label].filter(Boolean).join(" · ")}
      photos={library.library?.entries.flatMap((entry) => entry.kind === "media" && entry.src ? [{ id: entry.id, label: entry.label, src: entry.src }] : []) ?? []}
      replacing={photoAction === "replace" || Boolean(photoPick.slot || photoPick.path)}
      trail={trailFor(photoPick.path, "bw-photo-trail")}
      busy={photoStatus.busy}
      error={photoStatus.error}
      sharedCount={pickedEntry?.usedOn.length}
      onClose={() => { setPhotoPick(null); setPhotoAction(""); setPhotoStatus({ busy: false, error: null }); }}
      onReplace={() => { setPhotoAction("replace"); setPhotoStatus({ busy: false, error: null }); }}
      onDescribe={(alt) => {
        if (!photoPick.recordId) {
          openPhotoComment("Describe this photo: ");
          return;
        }
        void library.save(photoPick.recordId, { alt }).then(() => setPhotoPick(null));
      }}
      onSelectPhoto={(entry) => { void swapPhoto(photoPick, entry.id, entry.label, async () => site.draftId); }}
      onUpload={(files) => {
        setReplacementTarget(photoPick);
        setPendingPhotos({ files, from: "content" });
      }}
      onCrop={() => {
        if (!photoPick.recordId) { openPhotoComment("Crop this photo to show "); return; }
        setPhotoAction("crop"); setPhotoStatus({ busy: false, error: null });
      }}
      onComment={() => openPhotoComment("")}
    >
      {pendingPhotos?.from === "content" ? photoAdd : null}
    </PhotoActions>
  ) : null;
  // Crop from the uncropped original, served from this origin so the canvas can read it.
  const cropSource = pickedEntry?.originalSrc ?? pickedEntry?.src ?? photoPick?.src ?? "";
  const photoCrop = photoPick && photoAction === "crop" && photoPick.recordId ? (
    <PhotoCrop
      wide={wide}
      top={Math.max(8, photoPick.bottom + 10)}
      left={Math.max(8, photoPick.left)}
      src={cropSource}
      previous={pickedEntry?.crop}
      slotAspect={photoPick.width && photoPick.height ? photoPick.width / photoPick.height : 1}
      onCancel={() => setPhotoAction("")}
      onSave={async (crop) => {
        const asset = await api.uploadPhoto(siteId, await croppedPhoto(cropSource, crop));
        const action = await api.submitAction(siteId, { actionId: `crop-${Date.now().toString(36)}`, baseDraftId: site.draftId, command: { kind: "media.crop", recordId: photoPick.recordId!, asset, crop } });
        if (!["queued", "running", "succeeded"].includes(action.state)) throw new Error("Not saved");
        await Promise.all([refreshAll(), library.retry()]);
        setPhotoPick(null); setPhotoAction("");
      }}
    />
  ) : null;

  return (
    <div ref={keyboardRoot} className="bw" data-mode={mode} data-panel={panelOpen ? "open" : "closed"} data-drawer={drawerOpen && dockedDrawer ? "open" : "closed"} data-empty={!hasRoutedPages || undefined}>
      <WorkspaceBar
        name={siteName ?? (!hasRoutedPages ? "Your new website" : address)}
        address={address}
        status={status}
        line={statusLine}
        publish={publish}
        {...(shopOpen ? { shopArea: { href: shop?.settings ? `/_builder/preview/${siteId}/${site.draftId}${shop.settings.base}${shopProductId ? `/${shopProductShown?.slug ?? ""}` : ""}` : undefined } } : {})}
        {...(blogOpen && blog?.settings ? { blogArea: { href: liveStatus(workspace).kind === "never-published" ? undefined : `${workspace.manifest.canonicalOrigin.replace(/\/$/, "")}${blog.settings.base}` } } : {})}
        mode={mode}
        onModeChange={MODE_SWITCH ? setMode : undefined}
        liveHref={liveStatus(workspace).kind === "never-published" ? undefined : workspace.manifest.canonicalOrigin}
        undo={{ available: undo.state.phase === "offered", onUndo: () => void undo.undo() }}
        select={canEdit && mode === "advanced" ? {
          active: editingNow,
          onToggle: () => {
            setPointer((now) => !now);
            setCommenting(false);
            closeComment();
            setPicked(null);
            setTextSave("idle");
          },
        } : undefined}
        comment={canEdit ? {
          active: mode === "simple" ? pointingNow : commentingNow,
          draftCount: comments.drafts.length,
          onToggle: () => {
            if (mode === "simple") { setPointer((now) => !now); setPhotoPick(null); return; }
            setCommenting((now) => !now);
            setPointer(false);
            setPicked(null);
            closeComment();
          },
        } : undefined}
        menuOpen={drawerOpen}
        onMenu={() => {
          setDrawerOpen((now) => !now);
        }}
        onPreview={canPreview ? () => { setDrawerOpen(false); setPreviewing(true); } : undefined}
      />
      {previewing && (
        <PreviewView
          root={canvasRoot}
          path={shownPath}
          home={home}
          title={currentPage?.title ?? "your website"}
          startSize={startingPreviewSize(wide)}
          onClose={closePreview}
        />
      )}

      <SiteDrawer
        open={drawerOpen}
        docked={dockedDrawer}
        onClose={() => setDrawerOpen(false)}
        siteName={siteName ?? (!hasRoutedPages ? "Your new website" : address)}
        cardSiteHref={cardSiteHref}
        account={account}
        logoutHref={logoutHref}
        top={<AreaSwitcher
          loading={blogAvailable && blogRead.busy && !blog}
          error={!blog ? blogRead.error : null}
          onRetry={() => void reloadBlog()}
          current={area}
          rows={areaRows}
          label={switcherLabel(area, blog?.settings ?? null, address, shop?.settings)}
          siteName={siteName ?? address}
          hint={shortcutHint(areaRows.areas)}
          onPickArea={switchArea}
          onAdd={(feature) => { if (feature === "blog") openAddBlog(); else if (feature === "shop") { leaveDrawer(); setShopAddOpen(true); } }}
          onSiteSettings={() => openSiteSettings()}
          settingsDetail={settingsSubtitle(addressValue({ domain: publishing.status?.domain, platformAddress: publishing.status ? displayOrigin(publishing.status.origin) : address }), publishing.status?.entitled)}
        />}
        modeControls={MODE_SWITCH ? <div className="bw-drawer-mode"><ModeSwitch mode={mode} onModeChange={setMode} /></div> : undefined}
        management={<LeftPanel
          onOpen={openManager}
          area={area}
          shop={shop?.settings ? { section: shopSection, onSection: (next) => { if (!leaveShop()) return; setShopSection(next); setShopProductId(null); leaveDrawer(); }, onDesignPages: designShopPages, onOpenShop: () => switchArea("shop"), onOpenSettings: () => { setShopSection("settings"); setShopProductId(null); switchArea("shop"); } } : undefined}
          blog={blogAvailable && blog?.settings ? {
            counts: { posts: blog.total ?? blog.posts.length, tags: blog.tags.length },
            section: blogSection,
            onSection: (next) => { setBlogSection(next); setBlogPostId(null); leaveDrawer(); if (!wide) setPanelOpen(false); },
            onOpenBlog: () => switchArea("blog"),
            onOpenSettings: () => { setBlogSection("settings"); setBlogPostId(null); switchArea("blog"); },
            onDesignPages: designBlogPages,
          } : null}
          onOpenHistory={openHistory}
          pages={pages}
          pageTitles={pageTitles}
          currentPath={shownPath}
          sections={sections}
          markers={markers.length ? new Map(markers.map((marker) => [marker.nodeId, marker.number])) : undefined}
          selectedNodeId={selection?.nodeId}
          onPage={(path) => { if (!leaveShop()) return; setBlogOpen(false); selectPage(path); leaveDrawer(); }}
          onSection={(nodeId) => { setSelection(sections?.find((section) => section.nodeId === nodeId) ?? null); leaveDrawer(); }}
          onNewPage={() => toChat("Add a new page for ")}
          onRenamePage={(path) => toChat(`Rename the page ${path} to `)}
          onAddSection={() => {
            const after = selection ?? sections?.at(-1) ?? null;
            if (after) setInsertAfter(after);
          }}
          showAddSection={mode === "advanced"}
        />}
      />

      {shopAvailable && shopAddOpen && <ManagementModal title="Add a shop" description="Sell physical products and digital downloads." onClose={() => setShopAddOpen(false)}><ShopSettingsView api={shopApi} siteId={siteId} shop={shop ?? { settings: null, rowVersion: 0, version: 0, products: [], categories: [], customizationSets: [], stock: [], files: [] }} address={address} timeZone={workspace.seo?.timeZone ?? null} setup onCancel={() => setShopAddOpen(false)} onSaved={(next) => { setShop(next); setShopAddOpen(false); setArea("shop"); if (!wide) setPanelOpen(false); }} /></ManagementModal>}
      {blogAvailable && blogAddOpen && <ManagementModal title="Add a blog" description="A place to write and publish your stories." onClose={() => setBlogAddOpen(false)}><BlogAdd api={api} siteId={siteId} suggestion={blogAddSuggestion} address={address} onAdded={async ({ name, base, menu, latest }) => { await refreshAll(); await reloadBlog(); setBlogOpen(true); setPanelOpen(true); const request = blogAddRequest({ name, base, menu, latest }); if (!chat.send(request)) chat.setDraft(request); }} onClose={() => setBlogAddOpen(false)} /></ManagementModal>}

      {managerView && (
        <ManagementModal
          title={managerView === "content" ? "Content" : "Brand"}
          description={managerView === "content"
            ? "The photos, text and lists your website is built from."
            : "The voice, colours and typefaces that guide your website."}
          onClose={() => setManagerView(null)}
        >
          {managerView === "content" ? (
            <Library library={library} onPickPhotos={(files) => setPendingPhotos({ files, from: "content" })} photoAdd={pendingPhotos?.from === "content" ? photoAdd : undefined} />
          ) : (
            <BrandView
              workspace={workspace}
              onSave={saveBrand}
              onAsk={(text) => {
                setManagerView(null);
                askAdmin(text);
              }}
            />
          )}
        </ManagementModal>
      )}

      {siteSettings && (
        <SiteSettings
          initialGroup={siteSettings.group}
          workspace={workspace}
          publishing={publishing}
          address={address}
          upload={(icon) => api.uploadPhoto(siteId, icon)}
          photos={library.library?.entries.flatMap((entry) => entry.kind === "media" && entry.src ? [{ id: entry.id, label: entry.label, src: entry.src, mediaType: entry.mediaType }] : []) ?? []}
          onSaveDetails={saveSiteDetails}
          onAskAdmin={(text) => {
            setSiteSettings(null);
            askAdmin(text);
          }}
          onAddressChanged={() => void refreshAll()}
          onClose={() => setSiteSettings(null)}
        />
      )}

      <main className="bw-stage">
        {hostBanner}
        {shopOpen && shopRead.busy && shop && <LoadingActivity label="Refreshing shop…" />}
        {shopOpen && shop && shopRead.error && <AreaError message={shopRead.error} onRetry={() => void reloadShop()} />}
        {blogOpen && blog && blogRead.error && <AreaError message={blogRead.error} onRetry={() => void reloadBlog()} />}
        {disconnected && (
          <div className="bw-notice" data-tone="attention" role="status">
            <span>Can’t reach StackJe. You’re seeing the last version that loaded, which may be out of date.</span>
            <button type="button" className="bw-btn bw-btn--quiet" onClick={() => void refresh()} disabled={isRefreshing}>
              {isRefreshing && <Spinner />}
              {isRefreshing ? "Trying…" : "Try again"}
            </button>
          </div>
        )}
        {shopAvailable && shopOpen && !shop ? <AreaPage area="shop"><AreaHead designLabel="Design shop pages" onDesign={designShopPages} />{shopRead.error ? <AreaError message={shopRead.error} onRetry={() => void reloadShop()} /> : shopSection === "settings" ? <ShopFieldsSkeleton label="Loading shop settings" /> : <ProductListSkeleton label={shopSection === "orders" ? "Loading orders" : shopSection === "categories" ? "Loading categories" : "Loading products"} />}</AreaPage> : shopAvailable && shopOpen && shop?.settings ? <ShopView orderId={shopOrderId} onOrderId={setShopOrderId} onProductShown={setShopProductShown} publicOrigin={publishing.status?.origin ?? workspace.manifest.canonicalOrigin} previewRoot={`/_builder/preview/${siteId}/${site.draftId}`} onDirtyChange={setShopDirty} api={shopApi} builderApi={api} siteId={siteId} shop={shop} onSaved={setShop} section={shopSection} onSection={setShopSection} productId={shopProductId} onProductId={setShopProductId} onSelectedOrder={setShopSelectedOrder} orderChangeId={chat.conversation?.messages.flatMap(message => message.changes ?? []).filter(change => change.area === "shop" && change.entity === "order").at(-1)?.id} address={address} language={workspace.seo?.language ?? "en"} timeZone={workspace.seo?.timeZone ?? null} onDesignPages={designShopPages} /> : blogAvailable && blogOpen && !blog ? <AreaPage area="blog"><AreaHead designLabel="Design blog pages" onDesign={designBlogPages} />{blogRead.error ? <AreaError message={blogRead.error} onRetry={() => void reloadBlog()} /> : blogSection === "settings" ? <FieldsSkeleton label="Loading blog settings" /> : <ListSkeleton label={blogSection === "tags" ? "Loading tags" : "Loading posts"} />}</AreaPage> : blogAvailable && blogOpen && blog?.settings ? <BlogView refreshing={blogRead.busy} onPrepareAsk={(request, onSent) => { selectionRequest.prepare(request, onSent); setPanelPost(false); setPanelOpen(true); setPanelTab("chat"); }} onDirtyChange={setBlogDirty} onOpenPostReader={reader => { openPostReader.current = reader; }} onRefine={text => chat.send(text)} onPostShown={setBlogPostTitle} api={api} siteId={siteId} draftId={site.draftId} blog={blog} reload={async () => { await refreshAll(); await reloadBlog(); }} section={blogSection} onSection={setBlogSection} postId={blogPostId} onPostId={setBlogPostId} onDesignPages={designBlogPages} websiteChanges={changeCount} onAsk={(text, post) => { setPanelPost(false); setPanelOpen(true); return chat.send(text, [], post); }} messages={chat.conversation?.messages ?? []} onEditing={setBlogEditing} onOpenSettings={() => { setPanelPost(true); setPanelOpen(true); }} panelPost={panelPost} fonts={workspace.look?.fonts} address={address} siteName={siteName ?? undefined} timeZone={workspace.seo?.timeZone ?? null} language={workspace.seo?.language ?? null} /> : !hasRoutedPages && shownPath !== NOT_FOUND_PREVIEW_PATH ? (
          <BlankCanvas building={chat.conversation?.status === "replying" && Boolean(chat.conversation.progress?.firstBuild)} firstLook={chat.conversation?.progress?.firstLook} />
        ) : (
          <PreviewFrame
            root={canvasRoot}
            path={requestedPath}
            navigation={navigation?.count ?? 0}
            title={currentPage?.title ?? "your website"}
            onPathChange={(path) => {
              // The isolated preview can report its private proxy path. Only routes in this
              // workspace are page selections; otherwise retain the requested page.
              if (!pages.some((page) => page.path === path)) return;
              // Following a link inside the page loads the draft it showed; move on to the latest.
              if (path !== currentPath && currentPath !== null) catchUpFrame();
              setCurrentPath(path);
              if (path !== pagePath) setPagePath(path, true);
            }}
            mode={bridgeMode({ mode, pointer, commenting: commentingNow, canEdit })}
            onPick={(pick) => {
              setPicked(pick);
              setPhotoPick(null);
              setTextSave("idle");
              // Point to change moved down to the words: they replace the note that was open.
              if (pick.path) { setNewPin(null); setOpenPin(null); }
            }}
            onSelect={(value) => setSelection(value)}
            onSections={setSections}
            onPages={(labels) => setPreviewPageTitles((current) => ({ ...current, ...Object.fromEntries(labels.map(({ path, label }) => [path, label])) }))}
            selectNode={selection?.nodeId}
            onPhoto={(value) => {
              setPhotoPick(value);
              setPicked(null);
              if (value.path) { setNewPin(null); setOpenPin(null); setPhotoAction(""); setPhotoStatus({ busy: false, error: null }); }
            }}
            overrides={overrides}
            commenting={commentingNow}
            onDrop={(drop) => {
              setOpenPin(null);
              setNewPin(drop);
              // Point to change selected a section or a card: its note replaces a text or photo popover.
              if (drop.path) { setPicked(null); setPhotoPick(null); }
            }}
              pins={pins}
              onPinOpen={(id, at) => {
                if (id.startsWith("change-")) { setFocusedChange(id.slice("change-".length)); return; }
              if (id === "new-comment") return;
              setNewPin(null);
              setOpenPin({ id, at });
            }}
            reveal={reveal}
            pointStep={pointStep}
            onMissing={comments.reportMissing}
            // The page scroll each popover below was placed at (the section toolbar and gaps follow the page already).
            overlayScroll={commentCard ? (noteDocked ? undefined : (newPin ?? openPin?.at)?.scrollY) : (photoCrop ?? photoActions) ? photoPick?.scrollY : insertAfter ? insertAfter.scrollY : picked?.scrollY}
            overlay={commentCard ?? photoCrop ?? photoActions ?? (insertAfter ? <AddSectionPicker
              wide={wide}
              after={insertAfter.label}
              collections={insertCollections(library.library?.entries ?? [], sections ?? [])}
              top={Math.max(16, insertAfter.rect.bottom + 16)}
              left={Math.max(16, insertAfter.rect.left + 16)}
              onClose={() => setInsertAfter(null)}
              onAdd={(picks) => {
                const drop = dropAt(insertAfter, wide ? "desktop" : "phone");
                void Promise.all(picks.map((pick) => comments.create({ id: newCommentId(), pagePath: shownPath, nodeId: drop.nodeId, ...(drop.itemId ? { itemId: drop.itemId } : {}), x: drop.x, y: drop.y, viewport: drop.viewport, note: addSectionNote(pick), request: { kind: "add-section", pick } })));
              }}
            /> : editingNow && sections && !picked ? <div className="bw-insert-gaps" aria-label="Add a section between sections">{sectionGaps(sections).filter((gap) => gap.below.nodeId !== selection?.nodeId).map((gap) => <button
              key={`${gap.above.nodeId}-${gap.below.nodeId}`}
              type="button"
              className="bw-insert-gap"
              data-nearby={selection?.nodeId === gap.above.nodeId || selection?.nodeId === gap.below.nodeId || undefined}
              style={{ top: gap.top, left: gap.left }}
              aria-label={`Add a section after ${gap.above.label}`}
              onClick={() => setInsertAfter(gap.above)}
            ><Plus size={16} aria-hidden="true" /></button>)}{selection && sections.some((entry) => entry.nodeId === selection.nodeId) ? (() => {
              const section = sections.find((entry) => entry.nodeId === selection.nodeId) ?? selection;
              return <SectionToolbar
                selection={selection}
                section={section}
                onComment={() => setNewPin(dropAt(selection, wide ? "desktop" : "phone"))}
                onAsk={() => toChat(`About the “${selection.label}” section: `)}
                onEdit={() => { setPanelTab("section"); setPanelOpen(true); }}
                onAddBelow={() => setInsertAfter(section)}
              />;
            })() : null}</div> : (
              editingNow && selection && (!sections || sections.some((entry) => entry.nodeId === selection.nodeId)) && !picked ? (() => {
                const section = sections?.find((entry) => entry.nodeId === selection.nodeId) ?? selection;
                return (
                  <SectionToolbar
                    selection={selection}
                    section={section}
                    onComment={() => setNewPin(dropAt(selection, wide ? "desktop" : "phone"))}
                    onAsk={() => toChat(`About the “${selection.label}” section: `)}
                    onEdit={() => { setPanelTab("section"); setPanelOpen(true); }}
                    onAddBelow={() => setInsertAfter(section)}
                  />
                );
              })() : (editingNow || pointingNow) && picked ? (
                <TextEditor
                  pick={picked}
                  label={FIELDS[picked.bind.split(".")[1] ?? ""] ?? "Text"}
                  note={textUsage(picked).note}
                  onlyHereAvailable={textUsage(picked).multiple}
                  saving={textSave === "saving"}
                  error={textSave === "failed"}
                  onSave={(text, onlyHere) => void saveText(picked, text, false, onlyHere)}
                  onCancel={() => setPicked(null)}
                  simple={mode === "simple"}
                  trail={trailFor(picked.path, "bw-text-editor-title")}
                  onAskRewrite={() => {
                    const text = picked.text;
                    setPicked(null);
                    toChat(`Rewrite “${text}” on ${currentPage?.title ?? "this page"}: `);
                  }}
                  onComment={() => {
                    if (picked.nodeId) {
                      setNewPin({
                        nodeId: picked.nodeId,
                        ...(picked.itemId ? { itemId: picked.itemId } : {}),
                        x: picked.x ?? 0.5,
                        y: picked.y ?? 0.5,
                        viewport: wide ? "desktop" : "phone",
                        top: picked.bottom,
                        bottom: picked.bottom,
                        left: picked.left,
                        scrollY: picked.scrollY,
                        ...(picked.path ? { path: picked.path } : {}),
                      });
                    }
                    setPicked(null);
                  }}
                />
              ) : undefined))}
            hint={!selection && !picked && !photoPick && ((pointingNow && !hintsDismissed.point) || (editingNow && !hintsDismissed.select)) ? (
              <div className="bw-hint-point">
                <span>
                  {pointingNow
                    ? touchScreen ? "Tap to select. Tap inside it to go deeper, or double-tap for the words or photo" : "Click to select. Click inside it to go deeper, or double-click for the words or photo"
                    : `${wide ? "Click" : "Tap"} a section to select it. ${wide ? "Double-click" : "Tap"} its text to edit it`}
                </span>
                <button
                  type="button"
                  aria-label="Dismiss hint"
                  onClick={() => {
                    const which = pointingNow ? "point" : "select";
                    try { localStorage.setItem(`bw-hint-${which}`, "1"); } catch { /* storage may be unavailable */ }
                    setHintsDismissed((now) => ({ ...now, [which]: true }));
                  }}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined}
            onReturnHome={() => selectPage(home)}
            banner={reviewBanner ?? (commentingNow ? <div className="bw-strip"><span>Add all your comments, then send them together</span><button type="button" className="bw-btn" onClick={() => setCommenting(false)}>{comments.drafts.length ? `Review ${comments.drafts.length}` : "Done"}</button></div> : undefined)}
            tone={versionShown ? "proposal" : undefined}
            badge={versionShown ? "Earlier version, not restored" : undefined}
          />
        )}
      </main>

      {mode === "advanced" && !wide && (
        <MobileTools
          onPages={() => setDrawerOpen(true)}
          select={canEdit ? {
            active: editingNow,
            onToggle: () => {
              setPointer((now) => !now);
              setCommenting(false);
              closeComment();
              setPicked(null);
              setTextSave("idle");
            },
          } : undefined}
          comment={canEdit ? {
            active: commentingNow,
            onToggle: () => {
              setCommenting((now) => !now);
              setPointer(false);
              setPicked(null);
              closeComment();
            },
          } : undefined}
          onAdd={() => {
            const after = selection ?? sections?.at(-1) ?? null;
            if (after) setInsertAfter(after);
          }}
          undo={{ available: undo.state.phase === "offered", onUndo: () => void undo.undo() }}
        />
      )}

      <AdminPanel
        open={panelOpen}
        title={publishView ? publishView.title : changesOpen ? "Changes" : "Stax"}
        icon={publishView ? (publishView.tone ? <PublishMark tone={publishView.tone} /> : undefined) : !changesOpen ? <Stax variant="mini" size={36} mood={mood} className="bw-panel-stax" /> : undefined}
        subtitle={publishView ? publishView.subtitle : changesOpen ? "Everything is saved. Visitors see changes once you publish." : !hasRoutedPages ? "Free to build. Publish when you’re ready." : undefined}
        onClose={() => setPanelOpen(false)}
        exits={publishView ? {
          onBackToStax: () => {
            leavePublish();
            setChangesOpen(false);
          },
          onClose: () => {
            leavePublish();
            setPanelOpen(false);
          },
        } : undefined}
        fullScreen={mode === "simple" && !publishView && !changesOpen}
        onBack={changesOpen && !publishView ? () => setChangesOpen(false) : undefined}
        view={publishView ? (liveOpen ? "live" : `publish-${state.phase}`) : changesOpen ? "changes" : blogOpen && editingPost && panelPost ? "post" : selectedPanelTab}
        tabs={!publishView && !changesOpen && blogOpen && editingPost ? <div className="bw-panel-tabs" role="tablist" aria-label="Post and Stax"><button type="button" role="tab" aria-selected={panelPost} onClick={() => setPanelPost(true)}>Post</button><button type="button" role="tab" aria-selected={!panelPost} onClick={() => setPanelPost(false)}>Chat</button></div> : !publishView && !changesOpen && mode === "advanced" ? (
          <div className="bw-panel-tabs" role="tablist" aria-label="Panel views" onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            const index = availableTabs.indexOf(selectedPanelTab);
            const next = availableTabs[(index + (event.key === "ArrowRight" ? 1 : -1) + availableTabs.length) % availableTabs.length]!;
            selectPanelTab(next);
            requestAnimationFrame(() => document.getElementById(`bw-panel-tab-${next}`)?.focus());
          }}>
            {availableTabs.map((tab) => <button key={tab} id={`bw-panel-tab-${tab}`} type="button" role="tab" aria-selected={selectedPanelTab === tab} aria-controls={`bw-panel-view-${tab}`} tabIndex={selectedPanelTab === tab ? 0 : -1} onClick={() => selectPanelTab(tab)}>{tab === "section" ? currentSection!.label : tab === "comments" ? <>Comments{comments.drafts.length > 0 && <span className="bw-tab-count">{comments.drafts.length}</span>}</> : "Chat"}</button>)}
          </div>
        ) : undefined}
        footer={publishView ? publishView.footer : changesOpen ? (publish.tone === "action" ? <div className="bw-changes-footer"><button type="button" className="bw-btn bw-btn--primary bw-btn--action" onClick={publish.onClick} disabled={publish.disabled}>{publish.label}</button></div> : undefined) : blogOpen && editingPost && panelPost ? undefined : mode === "advanced" && selectedPanelTab === "section" ? (undo.state.phase === "offered" ? <div className="bw-inspector-footer"><span>Saved</span><button type="button" className="bw-btn bw-btn--text" onClick={() => void undo.undo()}>Undo</button></div> : undefined) : mode === "advanced" && selectedPanelTab !== "chat" ? undefined : <div className="bw-panel-composer">{shopOpen && <div className="bw-panel-context">In Shop · {shopSection[0]!.toUpperCase() + shopSection.slice(1)}{shopSection === "orders" && shopSelectedOrder ? ` · Order #${shopSelectedOrder.number}` : shopProductId ? ` · ${shopProductShown?.name ?? "Product"}` : ""}</div>}{blogOpen && <div className="bw-panel-context">In Blog · {blogSection[0]!.toUpperCase() + blogSection.slice(1)}{blogPostId ? ` · ${blogPostTitle ?? "Post"}` : ""}</div>}{compactTray}<Composer selection={selectionRequest.pending?.request} onCancelSelection={selectionRequest.cancel} onSend={selectionRequest.submit} chat={chat} onPickPhotos={(files) => setPendingPhotos({ files, from: "chat" })} references={references} onOpenPanel={openStaxPanel} blogAvailable={blogAvailable} /></div>}
      >
        {publishView
          ? publishView.body
          : changesOpen ? (
            <ChangesView
              workspace={workspace}
              status={status}
              rows={changeRows}
              sections={sections ?? []}
              currentFile={shownFile}
              pageTitle={pageTitles[shownPath] ?? currentPage?.title ?? "Home"}
              focusedId={focusedChange}
              onUndoChange={(actionId) => submitInstant("undo", { kind: "change.undo", actionId })}
              onDiscard={() => workspace.liveDraftId ? submitInstant("discard", { kind: "draft.restore", fromDraftId: workspace.liveDraftId }) : Promise.resolve(false)}
              onRevealSection={(nodeId) => setSelection(sections?.find((entry) => entry.nodeId === nodeId) ?? null)}
              previewing={versionShown?.draftId ?? null}
              onPreview={(entry) => {
                setVersionPreview(entry);
                setRestoring("idle");
              }}
              onDraft={() => setVersionPreview(null)}
              serving={serving}
              onRestorePart={restorePart}
            />
          )
          : blogOpen && editingPost && panelPost ? <div id="bw-blog-settings-slot" />
          : selectedPanelTab === "section" && currentSection ? (
            <div role="tabpanel" id="bw-panel-view-section" aria-labelledby="bw-panel-tab-section">
              <SectionInspector section={currentSection} pageTitle={pageTitles[shownPath] ?? currentPage?.title ?? "Home"} library={library} onRequest={requestAtSection} onVisibility={(hidden) => setSectionVisibility(currentSection, hidden)} onComment={() => setNewPin(dropAt(currentSection, wide ? "desktop" : "phone"))} />
            </div>
          ) : selectedPanelTab === "comments" ? (
            <div role="tabpanel" id="bw-panel-view-comments" aria-labelledby="bw-panel-tab-comments" className="bw-comments-panel">
              {comments.comments.length === 0 && !comments.notice ? <p className="bw-activity-empty">No comments yet. Choose Comment, then click anything on the page.</p> : <CommentTray variant="full" comments={comments} pageTitles={pageTitles} currentPath={shownPath} canSend={chat.ready && chat.conversation?.status === "ready"} placing={commentingNow} onShow={showComment} />}
            </div>
          ) : (
            <div role={mode === "advanced" ? "tabpanel" : undefined} id={mode === "advanced" ? "bw-panel-view-chat" : undefined} aria-labelledby={mode === "advanced" ? "bw-panel-tab-chat" : undefined}>
              <ConversationBody
              shopApi={shopApi} siteId={siteId}
              onOpenHistory={openHistory}
              onShowPost={blogOpen && editingPost && !wide ? () => { setPanelOpen(false); requestAnimationFrame(() => document.querySelector(".bw-blog-suggestion")?.scrollIntoView({ block: "center", behavior: "smooth" })); } : undefined}
              chat={chat}
              results={{
                comments: resultComments(comments.comments),
                card: (batch) => <OutcomeCard batch={batch} comments={comments.comments} undo={undo.state} onUndo={() => void undo.undo()} onShow={showComment} onRestore={restoreCommentVersion} onOpenHistory={openHistory} />,
              }}
              onShowComment={showComment}
              firstSite={!hasRoutedPages}
              onPickLogo={(files) => setPendingPhotos({ files: files.slice(0, 1), from: "chat" })}
              references={references}
              open={panelOpen}
              notice={hasRoutedPages && !hasHomePage(pages) ? "Your website needs a home page before it can be published. You can still preview these pages." : undefined}
              review={(
                <>
                  {pendingPhotos?.from === "chat" && photoAdd}
                </>
              )}
              />
            </div>
          )}
      </AdminPanel>

      {!(mode === "advanced" && selectedPanelTab === "section" && undo.state.phase === "offered") && <UndoToast state={undo.state} onUndo={() => void undo.undo()} onDismiss={undo.dismiss} />}
      {itemSaved && !textUndo && undo.state.phase === "idle" && (
        <div className="bw-toast" role="status">
          <ToastIcon tone="done" />
          <span className="bw-toast-text">{itemSaved}</span>
          <DismissButton onDismiss={() => setItemSaved(null)} />
          <ToastTimer key={itemSaved} ms={6_000} />
        </div>
      )}
      {textUndo && undo.state.phase === "idle" && (
        <div className="bw-toast" role="status">
          <ToastIcon tone="done" />
          <span className="bw-toast-text">{textUndo.label} saved to your draft.</span>
          <button
            type="button"
            className="bw-btn bw-toast-action"
            onClick={() => {
              const { bind, before, after } = textUndo;
              setTextUndo(null);
              void saveText({ bind, text: after, top: 0, bottom: 0, left: 0 }, before, true);
            }}
          >
            Undo
          </button>
          <DismissButton onDismiss={() => setTextUndo(null)} />
          <ToastTimer key={`${textUndo.bind}:${textUndo.after}`} ms={15_000} />
        </div>
      )}
      {/* A post suggestion already shows beside the editor with its own Replace / Keep mine, so it never peeks over them. */}
      {!panelOpen && peek.peek && peek.peek.card?.kind !== "post-rewrite" && undo.state.phase === "idle" && (
        <Peek
          message={peek.peek}
          onDismiss={peek.dismiss}
          onOpen={() => {
            peek.dismiss();
            openPanel();
          }}
        />
      )}

      {!panelOpen && (
        <button
          ref={bubble}
          type="button"
          className="bw-bubble"
          aria-controls="bw-admin-panel"
          aria-expanded={false}
          onClick={openPanel}
        >
          <Stax variant="mini" size={48} mood={mood} className="bw-bubble-stax" />
          {bubbleState.label}
          {unsent > 0 && <span className="bw-bubble-count" aria-label={unsent === 1 ? "1 comment not sent" : `${unsent} comments not sent`}>{unsent}</span>}
        </button>
      )}
    </div>
  );
}
